import { useTranslation } from 'react-i18next';

import type { Site } from '~/interfaces/site';
import type { Subscription } from '~/interfaces/subscriptions';
import { useCurrentPlan } from '~/routes/subscribe/$subscriberId/_components/v2/hooks/useCurrentPlan';
import { isInvalidSubscription } from '~/utils/isInvalidSubscription';

import { BillingContext, type BillingContextType } from './BillingContext';

type Props = {
  subscription: Subscription;
  site: Site;
  subscriberId: string;
  isLoggedIn: boolean;
  children: React.ReactNode;
};

/**
 * Owns the subscriber's personal billing surface on the General tab — the `BillingElement`
 * (invoices, payment method, "update billing" → Stripe portal). Self-calls the billing slice of
 * `useCurrentPlan` (deduped with SubscriptionActions' call via the React-Query cache). Renders
 * no UI; the colocated `BillingSection` child renders from context. Subscription lifecycle lives
 * in `SubscriptionActionsProvider` and the admin / group billing fan-out in
 * `AdminBillingProvider` — both stay out.
 */
export const BillingProvider = ({ subscription, site, subscriberId, isLoggedIn, children }: Props) => {
  const { t } = useTranslation();
  const publication = site.publication;
  const groupSubscriptionsEnabled = publication.group_subscriptions_enabled ?? false;
  const isSubscriptionInvalid = isInvalidSubscription(subscription);

  const {
    isPremiumTier,
    areAllPlansGifts,
    isInactive,
    isLoadingBillingSummary,
    isCreatingStripeSession,
    billingDetails,
    billingSummary,
    onClickUpdateBilling,
  } = useCurrentPlan({ isLoggedIn, site, subscription, subscriberId, groupSubscriptionsEnabled });

  // Same gate the inline BillingElement used: only premium, logged-in subscribers with a Stripe
  // customer and at least one non-gift, active paid tier see invoices + payment method.
  const showBillingDetails =
    isPremiumTier &&
    isLoggedIn &&
    !!subscription.stripe_customer_id &&
    !isSubscriptionInvalid &&
    !areAllPlansGifts &&
    !isInactive;

  // Shape the raw billing-summary map + details into the BillingElement data payload here so
  // the colocated BillingSection stays a pure render of `billingData`.
  const billingData: BillingContextType['billingData'] = {
    locale: publication.language,
    tierInvoices: Object.keys(billingSummary || {}).map((tierId) => {
      const paidTrialInfo = subscription.premium_tiers.find(
        (tier) => tier.id === tierId && tier.paid_trial_info,
      )?.paid_trial_info;
      return {
        tier_name: billingSummary?.[tierId]?.tier_name,
        last_invoice: billingSummary?.[tierId]?.last_invoice,
        next_invoice: billingSummary?.[tierId]?.next_invoice,
        paid_trial_info: paidTrialInfo ?? undefined,
      };
    }),
    billing_details: billingDetails && {
      name: billingDetails.name,
      email: billingDetails.email,
      phone: billingDetails.phone,
      tax_ids: Array.isArray(billingDetails.tax_ids)
        ? billingDetails.tax_ids.map((id) => (typeof id === 'string' ? id : id.value))
        : undefined,
      address: billingDetails.address,
      brand: billingDetails.brand,
      last4: billingDetails.last4,
    },
  };

  // Computed (not a plain lookup) billing translation: turns the first paid-trial tier's
  // length/unit into "3 months free trial". Lives here so BillingSection's translation map is
  // pure t() lookups.
  const trialDurationLabel = (() => {
    const info = subscription.premium_tiers.find((tier) => tier.paid_trial_info)?.paid_trial_info;
    if (!info?.trial_length || !info?.trial_unit) return '';
    const numLength = Number(info.trial_length);
    const singularUnit = info.trial_unit.replace(/s$/, '');
    const unitDisplay = numLength === 1 ? singularUnit : `${singularUnit}s`;
    const lengthDisplay = `${numLength} `;
    return `${lengthDisplay}${unitDisplay} ${t('billing.summary.paid_trial.trial_label')}`;
  })();

  const value: BillingContextType = {
    showBillingDetails,
    isLoadingBillingSummary,
    isUpdateButtonDisabled: isCreatingStripeSession,
    billingData,
    trialDurationLabel,
    onClickUpdateBilling,
  };

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
};
